import { v2_App, v2_createApp } from '@crocodille/common';
import { assertSome, isNone } from '@crocodille/util';
import { getToken, GetTokenParams } from 'next-auth/jwt';

import { initStorage } from './storage';

import type { IncomingMessage, ServerResponse } from 'http';
import type { NextApiRequest, NextApiResponse } from 'next';

const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return ''; // browser should use relative url
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`; // SSR should use vercel url
  }

  return `http://localhost:${process.env.PORT ?? 3000}`; // dev SSR should use localhost
};

interface GetAppContext {
  readonly locale?: string;
  readonly req?: IncomingMessage | NextApiRequest;
  readonly res?: ServerResponse | NextApiResponse;
}

const REQUEST_APP_KEY = Symbol('REQUEST_APP_KEY');

export function getApp(ctx: GetAppContext): v2_App {
  const version = assertSome(
    process.env.NEXT_PUBLIC_APP_VERSION,
    'NEXT_PUBLIC_APP_VERSION',
  );

  let app = (
    ctx?.req as
      | {
          [REQUEST_APP_KEY]?: v2_App;
        }
      | undefined
  )?.[REQUEST_APP_KEY];

  const req = ctx.req;

  if (isNone(app)) {
    app = v2_createApp({
      locale: assertSome(ctx.locale ?? 'cs', 'locale is required'),
      version,
      clientType: 'WEB',
      apiBaseUrl: getBaseUrl(),
      apiGetToken: req
        ? async () => {
            const authHeader = req.headers.authorization;
            const syliusCustomerHeader = (req.headers['x-customer'] ??
              req.headers['x-sylius-customer']) as string | undefined;

            if (authHeader && syliusCustomerHeader) {
              return {
                syliusApiToken: authHeader.replace('Bearer ', '').trim(),
                syliusCustomerId: syliusCustomerHeader,
              };
            }

            const appAuthCookie =
              'cookies' in req ? req.cookies['app_token'] : undefined;

            if (appAuthCookie) {
              try {
                const cookieValue = JSON.parse(appAuthCookie);

                return {
                  syliusApiToken: cookieValue.apiToken,
                  syliusCustomerId: cookieValue.id,
                };
              } catch {
                // Do nothing
              }
            }

            return await getToken({
              req: req as GetTokenParams['req'],
              raw: true,
              secret: process.env.NEXTAUTH_SECRET,
            });
          }
        : undefined,
      ...initStorage(ctx),
    });

    if (ctx.req) {
      (
        ctx.req as {
          [REQUEST_APP_KEY]?: v2_App;
        }
      )[REQUEST_APP_KEY] = app;
    }
  }

  return app;
}
